import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { OutsourcingPaymentTableService } from "services/outsourcing-payment-table";
import { formatVndIntegerInput, parseVndIntegerInput } from "utils";
import { numberToWords, getPrintTemplate } from "./components/printTemplate";

const createRow = () => ({
  fullName: "",
  identityNumber: "",
  jobName: "",
  workQuantity: "",
  unitPrice: "",
  grossAmount: "",
  taxWithheld: "",
  signature: "",
});

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const roundMoney = (value) => {
  const parsed = parseVndIntegerInput(value);
  return Number.isFinite(parsed) ? Math.round(parsed) : 0;
};

const fmt = (n) => new Intl.NumberFormat("vi-VN").format(roundMoney(n));

const computeRow = (row) => {
  const workQuantity = num(row.workQuantity);
  const hasUnitPrice = row.unitPrice !== "" && row.unitPrice !== null && row.unitPrice !== undefined;
  const unitPrice = hasUnitPrice ? roundMoney(row.unitPrice) : null;

  const grossAmount = hasUnitPrice
    ? roundMoney(workQuantity * (unitPrice || 0))
    : roundMoney(row.grossAmount);

  const taxWithheld = Math.min(Math.max(roundMoney(row.taxWithheld), 0), grossAmount);
  const netAmount = grossAmount - taxWithheld;

  return {
    ...row,
    workQuantity,
    unitPrice,
    grossAmount,
    taxWithheld,
    netAmount,
  };
};

const splitDateParts = (dateText = "") => {
  const [day = "", month = "", year = ""] = String(dateText).split("/");
  return {
    day: day.replace(/\D/g, "").slice(0, 2),
    month: month.replace(/\D/g, "").slice(0, 2),
    year: year.replace(/\D/g, "").slice(0, 4),
  };
};

const hasAnyDatePart = (parts) => Boolean(parts.day || parts.month || parts.year);
const hasAllDatePart = (parts) => Boolean(parts.day && parts.month && parts.year);

const sanitizeDatePart = (value, maxLength) => value.replace(/\D/g, "").slice(0, maxLength);

const composeDate = (parts) => {
  if (!hasAnyDatePart(parts)) return "";
  const day = parts.day.padStart(2, "0");
  const month = parts.month.padStart(2, "0");
  const year = parts.year;
  return `${day}/${month}/${year}`;
};

const validateDateParts = (parts, label) => {
  if (!hasAnyDatePart(parts)) return "";

  if (!hasAllDatePart(parts)) {
    throw new Error(`${label} chưa nhập đủ ngày/tháng/năm`);
  }

  const day = Number(parts.day);
  const month = Number(parts.month);
  const year = Number(parts.year);

  if (month < 1 || month > 12) {
    throw new Error(`${label} có tháng không hợp lệ`);
  }
  if (year < 1900 || year > 9999) {
    throw new Error(`${label} có năm không hợp lệ`);
  }

  const maxDays = new Date(year, month, 0).getDate();
  if (day < 1 || day > maxDays) {
    throw new Error(`${label} có ngày không hợp lệ`);
  }

  return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${String(year)}`;
};

function OutsourcingPaymentScheduleDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();

  const today = new Date();
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [header, setHeader] = useState({
    unit: "",
    department: "",
    docNumber: "",
    month: String(today.getMonth() + 1),
    year: String(today.getFullYear()),
    hirerName: "",
    hirerDepartmentAddress: "",
    requestTo: "",
    workPurpose: "",
    workLocation: "",
    workStartDate: "",
    workEndDate: "",
    attachmentNote: "",
  });
  const [rows, setRows] = useState([createRow()]);
  const [startDate, setStartDate] = useState({ day: "", month: "", year: "" });
  const [endDate, setEndDate] = useState({ day: "", month: "", year: "" });

  useEffect(() => {
    if (!id) return;

    (async () => {
      try {
        const response = await OutsourcingPaymentTableService.getById(id);
        const apiData = response.data ? response.data : response;

        const fallbackStart = apiData.workDuration?.split("-")?.[0]?.trim() || "";
        const fallbackEnd = apiData.workDuration?.split("-")?.[1]?.trim() || "";

        setHeader({
          unit: apiData.unit || "",
          department: apiData.department || "",
          docNumber: apiData.unitNumber || "",
          month: String(apiData.month || today.getMonth() + 1),
          year: String(apiData.year || today.getFullYear()),
          hirerName: apiData.hirerName || "",
          hirerDepartmentAddress: apiData.hirerDepartmentAddress || "",
          requestTo: apiData.requestTo || "",
          workPurpose: apiData.workPurpose || apiData.workContent || "",
          workLocation: apiData.workLocation || "",
          attachmentNote: apiData.attachmentNote || "",
          workStartDate: "",
          workEndDate: "",
        });
        setStartDate(splitDateParts(apiData.workStartDate || fallbackStart));
        setEndDate(splitDateParts(apiData.workEndDate || fallbackEnd));

        const mappedRows = (apiData.outsourcedWorkerPayments || []).map((emp) => ({
          fullName: emp.fullName || "",
          identityNumber: emp.identityNumber || "",
          jobName: emp.jobName || "",
          workQuantity: String(emp.workQuantity ?? ""),
          unitPrice: emp.unitPrice === null || emp.unitPrice === undefined ? "" : formatVndIntegerInput(emp.unitPrice),
          grossAmount: formatVndIntegerInput(emp.grossAmount ?? ""),
          taxWithheld: formatVndIntegerInput(emp.taxWithheld ?? ""),
          signature: emp.signature || "",
        }));

        setRows(mappedRows.length > 0 ? mappedRows : [createRow()]);
      } catch (error) {
        toast.error("Không thể tải dữ liệu bảng thuê ngoài");
      }
    })();
  }, [id]);

  const calculatedRows = useMemo(() => rows.map((row) => computeRow(row)), [rows]);

  const totals = useMemo(() => {
    return calculatedRows.reduce(
      (acc, row) => ({
        totalBeforeTax: acc.totalBeforeTax + roundMoney(row.grossAmount),
        totalTaxWithheld: acc.totalTaxWithheld + roundMoney(row.taxWithheld),
        totalNetPayment: acc.totalNetPayment + roundMoney(row.netAmount),
      }),
      {
        totalBeforeTax: 0,
        totalTaxWithheld: 0,
        totalNetPayment: 0,
      }
    );
  }, [calculatedRows]);

  const setHeaderField = (field, value) => {
    setHeader((prev) => ({ ...prev, [field]: value }));
  };

  const setDateField = (target, part, value) => {
    const sanitized = sanitizeDatePart(value, part === "year" ? 4 : 2);
    if (target === "start") {
      setStartDate((prev) => ({ ...prev, [part]: sanitized }));
      return;
    }
    setEndDate((prev) => ({ ...prev, [part]: sanitized }));
  };

  const padDatePartOnBlur = (target, part) => {
    if (part === "year") return;
    const setter = target === "start" ? setStartDate : setEndDate;
    setter((prev) => {
      const current = String(prev[part] || "");
      if (!current) return prev;
      return { ...prev, [part]: current.padStart(2, "0") };
    });
  };

  const setRowField = (index, field, value) => {
    setRows((prev) => {
      const next = [...prev];
      if (["unitPrice", "grossAmount", "taxWithheld"].includes(field)) {
        next[index] = { ...next[index], [field]: formatVndIntegerInput(value) };
        return next;
      }
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleAddRow = () => {
    setRows((prev) => [...prev, createRow()]);
  };

  const handleRemoveRow = (index) => {
    setRows((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const buildPayload = () => {
    const year = String(header.year || "").trim();
    const month = String(header.month || "").trim();
    const unitNumber = (header.docNumber || "").trim();
    const unit = (header.unit || "").trim();
    const department = (header.department || "").trim();
    const hirerName = (header.hirerName || "").trim();
    const hirerDepartmentAddress = (header.hirerDepartmentAddress || "").trim();
    const requestTo = (header.requestTo || "").trim();
    const workPurpose = (header.workPurpose || "").trim();
    const workLocation = (header.workLocation || "").trim();

    if (!/^\d{4}$/.test(year)) {
      throw new Error("Năm phải gồm 4 chữ số (ví dụ: 2026)");
    }
    if (!/^\d{1,2}$/.test(month) || Number(month) < 1 || Number(month) > 12) {
      throw new Error("Tháng không hợp lệ (chỉ từ 1 đến 12)");
    }
    if (!unitNumber) {
      throw new Error("Số chứng từ (Số) không được để trống");
    }
    if (!unit) {
      throw new Error("Đơn vị không được để trống");
    }
    if (!department) {
      throw new Error("Bộ phận không được để trống");
    }
    if (!hirerName) {
      throw new Error("Họ và tên người thuê không được để trống");
    }
    if (!hirerDepartmentAddress) {
      throw new Error("Bộ phận (hoặc địa chỉ) không được để trống");
    }
    if (!requestTo) {
      throw new Error("Mục 'Đề nghị ... cho thanh toán' không được để trống");
    }
    if (!workPurpose) {
      throw new Error("Nội dung công việc thuê không được để trống");
    }
    if (!workLocation) {
      throw new Error("Địa điểm thuê không được để trống");
    }

    const employees = calculatedRows
      .map((item) => ({
        fullName: item.fullName?.trim() || "",
        identityNumber: item.identityNumber?.trim() || "",
        jobName: item.jobName?.trim() || "",
        workQuantity: num(item.workQuantity),
        unitPrice: item.unitPrice === null ? null : roundMoney(item.unitPrice),
        grossAmount: roundMoney(item.grossAmount),
        taxWithheld: roundMoney(item.taxWithheld),
        signature: item.signature?.trim() || "",
      }))
      .filter((item) => item.fullName || item.identityNumber || item.jobName || item.grossAmount > 0);

    if (!employees.length) {
      throw new Error("Bảng thuê ngoài phải có ít nhất 1 dòng hợp lệ");
    }

    employees.forEach((item, index) => {
      if (!item.fullName || !item.identityNumber || !item.jobName || item.grossAmount <= 0) {
        throw new Error(`Dòng ${index + 1} chưa đủ Họ tên/CCCD-MST/Công việc/Thành tiền`);
      }
    });

    const workStartDate = validateDateParts(startDate, "Từ ngày");
    const workEndDate = validateDateParts(endDate, "Đến ngày");
    const workDuration =
      workStartDate || workEndDate ? `${workStartDate} - ${workEndDate}` : "";

    if (!workStartDate || !workEndDate) {
      throw new Error("Phải nhập đầy đủ Từ ngày và Đến ngày");
    }

    const [sd, sm, sy] = workStartDate.split("/").map(Number);
    const [ed, em, ey] = workEndDate.split("/").map(Number);
    const startDateObj = new Date(sy, sm - 1, sd);
    const endDateObj = new Date(ey, em - 1, ed);
    if (startDateObj.getTime() > endDateObj.getTime()) {
      throw new Error("Đến ngày phải lớn hơn hoặc bằng Từ ngày");
    }

    employees.forEach((item, index) => {
      const rowNo = index + 1;
      if (!item.fullName) {
        throw new Error(`Dòng ${rowNo}: Họ và tên người được thuê không được để trống`);
      }
      if (!item.identityNumber) {
        throw new Error(`Dòng ${rowNo}: CCCD/MST TNCN không được để trống`);
      }
      if (!item.jobName) {
        throw new Error(`Dòng ${rowNo}: Nội dung công việc không được để trống`);
      }
      if (Number(item.workQuantity) <= 0) {
        throw new Error(`Dòng ${rowNo}: Số công/khối lượng phải lớn hơn 0`);
      }
      if (item.unitPrice !== null && Number(item.unitPrice) <= 0) {
        throw new Error(`Dòng ${rowNo}: Đơn giá phải lớn hơn 0 (hoặc để trống nếu khoán)`);
      }
      if (Number(item.grossAmount) <= 0) {
        throw new Error(`Dòng ${rowNo}: Thành tiền phải lớn hơn 0`);
      }
      if (Number(item.taxWithheld) < 0) {
        throw new Error(`Dòng ${rowNo}: Tiền thuế khấu trừ không được âm`);
      }
      if (Number(item.taxWithheld) > Number(item.grossAmount)) {
        throw new Error(`Dòng ${rowNo}: Thuế khấu trừ không được lớn hơn Thành tiền`);
      }
    });

    return {
      year,
      month,
      unitNumber,
      unit,
      department,
      hirerName,
      hirerDepartmentAddress,
      requestTo,
      workPurpose,
      workContent: workPurpose,
      workLocation,
      workStartDate,
      workEndDate,
      workDuration,
      attachmentNote: String(header.attachmentNote || "").trim(),
      employees,
    };
  };

  const handleSubmit = async () => {
    try {
      setLoadingSubmit(true);
      const payload = buildPayload();
      if (!id) {
        await OutsourcingPaymentTableService.create(payload);
        toast.success("Tạo bảng thuê ngoài thành công!");
      } else {
        await OutsourcingPaymentTableService.update(id, payload);
        toast.success("Cập nhật bảng thuê ngoài thành công!");
      }
      navigation("/templates/outsourcing-payment-schedule");
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Lưu thất bại!");
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handlePrint = () => {
    const headerForPrint = {
      ...header,
      workStartDate: composeDate(startDate),
      workEndDate: composeDate(endDate),
    };

    const htmlContent = getPrintTemplate({
      headerInfo: headerForPrint,
      rows: calculatedRows,
      totals,
    });

    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const s = {
    container: {
      fontFamily: '"Times New Roman", Times, serif',
      color: "#000",
      padding: "20px",
      backgroundColor: "#fff",
      fontSize: "14px",
    },
    headerTop: { display: "flex", justifyContent: "space-between", marginBottom: "16px" },
    inputDotted: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      width: "240px",
      fontSize: "14px",
      fontFamily: "inherit",
      backgroundColor: "transparent",
      color: "#000",
    },
    inputDottedSmall: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      width: "70px",
      fontSize: "14px",
      fontFamily: "inherit",
      textAlign: "center",
      backgroundColor: "transparent",
      color: "#000",
    },
    inlineDateInput: {
      border: "none",
      outline: "none",
      width: 95,
      fontSize: "14px",
      fontFamily: "inherit",
      textAlign: "center",
      backgroundColor: "transparent",
      color: "#000",
      marginLeft: 6,
      marginRight: 6,
    },
    inlineDateText: {
      display: "inline-block",
      width: 95,
      textAlign: "center",
      marginLeft: 6,
      marginRight: 6,
    },
    table: { width: "100%", borderCollapse: "collapse", minWidth: 1420, fontSize: 12 },
    th: { border: "1px solid #000", padding: 4, textAlign: "center", fontWeight: "bold", verticalAlign: "middle" },
    td: { border: "1px solid #000", padding: 0, height: "30px" },
    inputText: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "left",
      fontSize: 13,
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 6px",
      display: "block",
      color: "#000",
    },
    inputNumber: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "right",
      fontSize: 13,
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 6px",
      display: "block",
      color: "#000",
    },
  };

  const startDateDisplay = containView ? composeDate(startDate) || ".../.../...." : "";
  const endDateDisplay = containView ? composeDate(endDate) || ".../.../...." : "";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={20}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Card>
              <SoftBox p={2} style={s.container}>
                <div style={s.headerTop}>
                  <div style={{ width: "50%" }}>
                    <div style={{ fontWeight: "bold", marginBottom: 5 }}>
                      Đơn vị: <input disabled={containView} style={s.inputDotted} value={header.unit} onChange={(e)=>setHeaderField("unit",e.target.value)} />
                    </div>
                    <div style={{ fontWeight: "bold" }}>
                      Bộ phận: <input disabled={containView} style={s.inputDotted} value={header.department} onChange={(e)=>setHeaderField("department",e.target.value)} />
                    </div>
                  </div>
                  <div style={{ textAlign: "center", width: "50%" }}>
                    <div style={{ fontWeight: "bold" }}>Mẫu số: 04 - LĐTL</div>
                    <div style={{ fontStyle: "italic", fontSize: "12px", marginTop: 4 }}>
                      (Kèm theo Thông tư số 99/2025/TT-BTC <br /> ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)
                    </div>
                  </div>
                </div>

                <div style={{ textAlign: "center", margin: "8px 0 4px" }}>
                  <h2 style={{ fontSize: "31px", fontWeight: "bold", margin: 0, textTransform: "uppercase" }}>
                    BẢNG THANH TOÁN TIỀN THUÊ NGOÀI
                  </h2>
                  <div style={{ fontWeight: "bold", marginTop: 2 }}>(Dùng cho thuê nhân công, thuê khoán việc)</div>
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10 }}>
                  <div>
                    Họ và tên người thuê: <input disabled={containView} style={{ ...s.inputDotted, width: 260 }} value={header.hirerName} onChange={(e)=>setHeaderField("hirerName",e.target.value)} />
                  </div>
                  <div>
                    Số: <input disabled={containView} style={{ ...s.inputDotted, width: 120 }} value={header.docNumber} onChange={(e)=>setHeaderField("docNumber",e.target.value)} />
                  </div>
                </div>

                <div style={{ marginTop: 10 }}>
                  Bộ phận (hoặc địa chỉ): <input disabled={containView} style={{ ...s.inputDotted, width: 420 }} value={header.hirerDepartmentAddress} onChange={(e)=>setHeaderField("hirerDepartmentAddress",e.target.value)} />
                </div>

                <div style={{ marginTop: 10 }}>
                  Đã thuê những công việc sau để:
                  <input disabled={containView} style={{ ...s.inputDotted, width: 230, marginLeft: 6, marginRight: 6 }} value={header.workPurpose} onChange={(e)=>setHeaderField("workPurpose",e.target.value)} />
                  tại địa điểm
                  <input disabled={containView} style={{ ...s.inputDotted, width: 140, marginLeft: 6, marginRight: 6 }} value={header.workLocation} onChange={(e)=>setHeaderField("workLocation",e.target.value)} />
                  từ ngày
                  {containView ? (
                    <span style={s.inlineDateText}>{startDateDisplay}</span>
                  ) : (
                    <span style={{ marginLeft: 6, marginRight: 6, display: "inline-flex", alignItems: "center" }}>
                      <input
                        placeholder=".."
                        style={{ ...s.inlineDateInput, width: 28, margin: 0 }}
                        value={startDate.day}
                        onChange={(e) => setDateField("start", "day", e.target.value)}
                        onBlur={() => padDatePartOnBlur("start", "day")}
                      />
                      <span>/</span>
                      <input
                        placeholder=".."
                        style={{ ...s.inlineDateInput, width: 28, margin: 0 }}
                        value={startDate.month}
                        onChange={(e) => setDateField("start", "month", e.target.value)}
                        onBlur={() => padDatePartOnBlur("start", "month")}
                      />
                      <span>/</span>
                      <input
                        placeholder="...."
                        style={{ ...s.inlineDateInput, width: 46, margin: 0 }}
                        value={startDate.year}
                        onChange={(e) => setDateField("start", "year", e.target.value)}
                      />
                    </span>
                  )}
                  đến ngày
                  {containView ? (
                    <span style={{ ...s.inlineDateText, marginLeft: 6, marginRight: 0 }}>
                      {endDateDisplay}
                    </span>
                  ) : (
                    <span style={{ marginLeft: 6, marginRight: 0, display: "inline-flex", alignItems: "center" }}>
                      <input
                        placeholder=".."
                        style={{ ...s.inlineDateInput, width: 28, margin: 0 }}
                        value={endDate.day}
                        onChange={(e) => setDateField("end", "day", e.target.value)}
                        onBlur={() => padDatePartOnBlur("end", "day")}
                      />
                      <span>/</span>
                      <input
                        placeholder=".."
                        style={{ ...s.inlineDateInput, width: 28, margin: 0 }}
                        value={endDate.month}
                        onChange={(e) => setDateField("end", "month", e.target.value)}
                        onBlur={() => padDatePartOnBlur("end", "month")}
                      />
                      <span>/</span>
                      <input
                        placeholder="...."
                        style={{ ...s.inlineDateInput, width: 46, margin: 0 }}
                        value={endDate.year}
                        onChange={(e) => setDateField("end", "year", e.target.value)}
                      />
                    </span>
                  )}
                </div>

                <div style={{ marginTop: 10, overflowX: "auto", border: "1px solid #ccc" }}>
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th style={s.th}>STT</th>
                        <th style={s.th}>Họ và tên người được thuê</th>
                        <th style={s.th}>CCCD/MST TNCN của người được thuê</th>
                        <th style={s.th}>Nội dung hoặc tên công việc thuê</th>
                        <th style={s.th}>Số công hoặc khối lượng công việc đã làm</th>
                        <th style={s.th}>Đơn giá thanh toán</th>
                        <th style={s.th}>Thành tiền</th>
                        <th style={s.th}>Tiền thuế khấu trừ</th>
                        <th style={s.th}>Số tiền còn lại được nhận</th>
                        <th style={s.th}>Ký nhận</th>
                        {!containView && <th style={s.th}>#</th>}
                      </tr>
                      <tr style={{ backgroundColor: "#fafafa" }}>
                        <th style={s.th}>A</th>
                        <th style={s.th}>B</th>
                        <th style={s.th}>C</th>
                        <th style={s.th}>D</th>
                        <th style={s.th}>1</th>
                        <th style={s.th}>2</th>
                        <th style={s.th}>3</th>
                        <th style={s.th}>4</th>
                        <th style={s.th}>5 = 3-4</th>
                        <th style={s.th}>E</th>
                        {!containView && <th style={s.th}></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {calculatedRows.map((row, index) => {
                        const isContractPackage = row.unitPrice === null;

                        return (
                          <tr key={`row-${index}`}>
                            <td style={{ ...s.td, textAlign: "center" }}>{index + 1}</td>
                            <td style={s.td}><input disabled={containView} style={s.inputText} value={row.fullName} onChange={(e)=>setRowField(index,"fullName",e.target.value)} /></td>
                            <td style={s.td}><input disabled={containView} style={{ ...s.inputText, textAlign: "center" }} value={row.identityNumber} onChange={(e)=>setRowField(index,"identityNumber",e.target.value)} /></td>
                            <td style={s.td}><input disabled={containView} style={s.inputText} value={row.jobName} onChange={(e)=>setRowField(index,"jobName",e.target.value)} /></td>
                            <td style={s.td}><input disabled={containView} type="text" inputMode="decimal" style={s.inputNumber} value={row.workQuantity} onChange={(e)=>setRowField(index,"workQuantity",e.target.value)} /></td>
                            <td style={s.td}>
                              <input
                                disabled={containView}
                                type="text"
                                inputMode="numeric"
                                placeholder="Khoán thì để trống"
                                style={s.inputNumber}
                                value={row.unitPrice === null ? "" : row.unitPrice}
                                onChange={(e)=>setRowField(index,"unitPrice",e.target.value)}
                              />
                            </td>
                            <td style={s.td}>
                              {containView ? (
                                <span style={{ ...s.inputNumber, lineHeight: "30px" }}>{fmt(row.grossAmount)}</span>
                              ) : (
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  disabled={!isContractPackage}
                                  style={{ ...s.inputNumber, opacity: !isContractPackage ? 0.7 : 1 }}
                                  value={row.grossAmount}
                                  onChange={(e)=>setRowField(index,"grossAmount",e.target.value)}
                                />
                              )}
                            </td>
                            <td style={s.td}><input disabled={containView} type="text" inputMode="numeric" style={s.inputNumber} value={row.taxWithheld} onChange={(e)=>setRowField(index,"taxWithheld",e.target.value)} /></td>
                            <td style={{ ...s.td, textAlign: "right", paddingRight: 6 }}>{fmt(row.netAmount)}</td>
                            <td style={s.td}><input disabled={containView} style={{ ...s.inputText, textAlign: "center" }} value={row.signature} onChange={(e)=>setRowField(index,"signature",e.target.value)} /></td>
                            {!containView && (
                              <td style={{ ...s.td, textAlign: "center" }}>
                                <button type="button" style={{ border: "none", background: "transparent", color: "red" }} onClick={() => handleRemoveRow(index)}>
                                  X
                                </button>
                              </td>
                            )}
                          </tr>
                        );
                      })}

                      <tr>
                        <td style={{ ...s.td, textAlign: "center" }}></td>
                        <td style={{ ...s.td, textAlign: "center", fontWeight: 700 }}>Cộng</td>
                        <td style={{ ...s.td, textAlign: "center" }}>x</td>
                        <td style={{ ...s.td, textAlign: "center" }}>x</td>
                        <td style={{ ...s.td, textAlign: "center" }}>x</td>
                        <td style={{ ...s.td, textAlign: "center" }}>x</td>
                        <td style={{ ...s.td, textAlign:"right", paddingRight:6, fontWeight:700 }}>{fmt(totals.totalBeforeTax)}</td>
                        <td style={{ ...s.td, textAlign:"right", paddingRight:6, fontWeight:700 }}>{fmt(totals.totalTaxWithheld)}</td>
                        <td style={{ ...s.td, textAlign:"right", paddingRight:6, fontWeight:700 }}>{fmt(totals.totalNetPayment)}</td>
                        <td style={{ ...s.td, textAlign: "center" }}>x</td>
                        {!containView && <td style={s.td}></td>}
                      </tr>
                    </tbody>
                  </table>
                </div>

                <SoftBox mt={2}>
                  Đề nghị{" "}
                  <input
                    disabled={containView}
                    style={{ ...s.inputDotted, width: 200 }}
                    value={header.requestTo || ""}
                    onChange={(e) => setHeaderField("requestTo", e.target.value)}
                  />{" "}
                  cho thanh toán số tiền: <strong>{fmt(totals.totalNetPayment)}</strong>
                </SoftBox>
                <SoftBox mt={1}>Số tiền (Viết bằng chữ): <strong>{numberToWords(totals.totalNetPayment)}</strong></SoftBox>
                <SoftBox mt={1} fontStyle="italic">
                  (Kèm theo{" "}
                  {containView ? (
                    <span>{header.attachmentNote || "..."}</span>
                  ) : (
                    <input
                      type="text"
                      disabled={containView}
                      value={header.attachmentNote || ""}
                      onChange={(e) => setHeaderField("attachmentNote", e.target.value)}
                      placeholder="..."
                      style={{
                        border: "none",
                        outline: "none",
                        background: "transparent",
                        fontFamily: "inherit",
                        fontSize: "14px",
                        fontStyle: "italic",
                        width: "3ch",
                        minWidth: "3ch",
                        color: "#000",
                        padding: 0,
                      }}
                    />
                  )}{" "}
                  chứng từ kế toán khác)
                </SoftBox>

                <SoftBox mt={3} display="flex" justifyContent="space-between">
                  <SoftButton variant="gradient" color="light" onClick={() => navigation(-1)}>
                    Trở về
                  </SoftButton>
                  <SoftBox display="flex" gap={2}>
                    {!containView && (
                      <SoftButton variant="outlined" color="dark" onClick={handleAddRow}>
                        THÊM DÒNG
                      </SoftButton>
                    )}
                    {containView ? (
                      <SoftButton variant="gradient" color="success" onClick={handlePrint}>
                        In Bảng Thuê Ngoài
                      </SoftButton>
                    ) : (
                      <SoftButton variant="gradient" color="dark" onClick={handleSubmit} disabled={loadingSubmit}>
                        {id ? "Lưu" : "Tạo"}
                      </SoftButton>
                    )}
                  </SoftBox>
                </SoftBox>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}

export default OutsourcingPaymentScheduleDetail;
